import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { SensorSOSEventsItem, SensorSOSEventsResponse, SubscriptionData, UpdateSubscriptionResponse } from "../../services/authinterface"
import Table from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV } from "../../utils/exportFunctions"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { Link } from "react-router-dom"
import { ResetIcon } from "../../icons/webSvg"

const SensorSOSEvents: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [sensorEvents, setSensorEvents] = useState<SensorSOSEventsItem[]>([])
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [totalItems, setTotalItems] = useState(100)
    const [search, setSearch] = useState("")
    const debounceText = useDebounce(search, 500)
    const [sort, setSort] = useState("DESC")
    const [sortCol, setSortCol] = useState("createdAt")
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    const [allOwners, setAllOwners] = useState<SubscriptionData[]>([])
    const [owner, setOwner] = useState("")
    const [city, setCity] = useState("")
    const [state, setState] = useState("")
    const [allLocations, setAllLocations] = useState<any>([])

    const fetchLocations = useCallback(async () => {
        try {
            const response = await request<any>({
                url: "/adminCommon/getAllLocations",
                method: "GET",
            })

            if (response.success) {
                setAllLocations(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        }
    }, [])

    useEffect(() => {
        fetchLocations()
    }, [fetchLocations])

    const fetchEvents = useCallback(async () => {
        try {
            dispatch(changeloaderState(true))
            const params = {
                page: page,
                limit: rowsPerPage,
                search: debounceText,
                sort: sort,
                sortCol: sortCol,
                startDate: startDate,
                endDate: endDate,
                city: city,
                state: state,
                ownerId: owner
            }
            const response = await request<SensorSOSEventsResponse>({
                url: "/adminCommon/getSensorEvents",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setSensorEvents(response.data)
                setTotalItems(response?.total)
            } else {
                toast.error("Failed to fetch Events: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        } finally {
            dispatch(changeloaderState(false))
        }
    }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol, startDate, endDate, owner, city, state])

    useEffect(() => {
        fetchEvents()
    }, [page, rowsPerPage, debounceText, sort, fetchEvents, sortCol, startDate, endDate, owner, city, state])

    const fetchOwners = useCallback(async () => {
        try {
            const params = {
                page: 1,
                limit: 100000,
                search: "",
            }
            const response = await request<UpdateSubscriptionResponse>({
                url: "/adminCustomers/allOwners",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setAllOwners(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        }
    }, [])

    useEffect(() => {
        fetchOwners()
    }, [fetchOwners])

    useEffect(() => {
        if (debounceText) {
            setPage(1)
        }
    }, [debounceText])

    const multipleFilters = [
        {
            label: "From",
            type: "datetime-local",
            value: startDate,
            onChange: (e: any) => {
                setStartDate(e.target.value)
            },
            max: endDate ? endDate : "",
            className: "userfilter sensor-owner sensorsList-filter",
        },
        {
            label: "To",
            type: "datetime-local",
            value: endDate,
            onChange: (e: any) => {
                setEndDate(e.target.value)
            },
            min:startDate ? startDate : "",
            className: "userfilter sensor-owner sensorsList-filter",
        },
        {
            label: "Owner",
            type: "select",
            value: owner,
            onChange: (e: any) => setOwner(e.target.value),
            className: "userfilter sensor-owner sensorsList-filter",
            option: [
                { value: "", label: "All" }, // 👈 first option
                ...allOwners.map((item) => ({
                    value: item.userId,
                    label:
                        item.first_name || item.last_name
                            ? `${item.first_name ?? ""} ${item.last_name ?? ""}`.trim()
                            : item.userAccounts?.email ?? "Unknown User",
                })),
            ],
        },
         {
            label: "City",
            type: "select",
            value: city,
            onChange: (e: any) => setCity(e.target.value),
            className: "userfilter sensor-model sensorsList-filter",
            option: [
                { value: "", label: "All" }, // 👈 first option
                ...Array.from(
                    new Map(
                        allLocations
                            .filter((item: any) => item.city && item.city.trim() !== "") // ✅ only valid cities
                            .map((item: any) => [item.city, item]) // ✅ city as key → removes duplicates
                    ).values()
                ).map((item: any) => ({
                    value: item.city,
                    label: item.city,
                })),
            ],
        },
        {
            label: "State",
            type: "select",
            value: state,
            onChange: (e: any) => setState(e.target.value),
            className: "userfilter sensor-model sensorsList-filter",
            option: [
                { value: "", label: "All" }, // 👈 first option
                ...Array.from(
                    new Map(
                        allLocations
                            .filter((item: any) => item.state && item.state.trim() !== "") // ✅ only valid states
                            .map((item: any) => [item.state, item]) // ✅ use state as key (removes duplicates)
                    ).values()
                ).map((item: any) => ({
                    value: item.state,
                    label: item.state,
                })),
            ],
        },
        {
            label: "Reset",
            type: "button",
            onClick: () => {
                setStartDate("")
                setEndDate("")
                setState("")
                setCity("")
                setOwner("")
                setSearch("")
            },
            icon: (
                <ResetIcon />
            ),
            className: "resetBtn Sensors-reset sensorsList-filter",
        },
    ]

    const columns = [
        {
            name: "Event Type",
            value: (event: SensorSOSEventsItem) => event?.eventName,
            id: "name",
            // className: "long-text-devices"
        },
        {
            name: "Execute Time",
            value: (event: SensorSOSEventsItem) => {
                if (!event?.executeTime) return "-";

                const date = new Date(event.executeTime);

                const options: Intl.DateTimeFormatOptions = {
                    timeZone: "America/New_York",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                    month: "short",
                    day: "numeric"
                };

                const formatted = date.toLocaleString("en-US", options);
                return `${formatted} ET`;
            },
            id: "name",
            // className: "long-text-devices"
        },
        {
            name: "Sensor Name",
            value: (event: SensorSOSEventsItem) => event?.sensorEvents?.is_sos === "1" ? event?.sensorEvents?.name : <Link className="device-link" to={`/location/device-details/${event?.sensorEvents?.id}`}>{event?.sensorEvents?.name}</Link>,
            id: "name",
            className: "long-text-devices"
        },
        {
            name: "Sensor or SOS",
            value: (event: SensorSOSEventsItem) => event?.sensorEvents?.is_sos === "1" ? "SOS" : "Sensor",
            id: "name",
            // className: "long-text-devices"
        },
        {
            name: "Location Name",
            value: (event: SensorSOSEventsItem) => event?.sensorEvents?.locationsSensor?.name ? <Link className="device-link" to={`/location/view/${event?.sensorEvents?.locationsSensor?.id}`}>{event?.sensorEvents?.locationsSensor?.name}</Link> : "-",
            id: "name",
            className: "long-text-devices"
        },
        {
            name: "Address",
            value: (event: SensorSOSEventsItem) => event?.sensorEvents?.locationsSensor?.address || "-",
            id: "name",
            className: "long-text-devices"
        },
        {
            name: "User Name",
            value: (event: SensorSOSEventsItem) =>
                !event?.sensorEvents?.userAccount?.first_name && !event?.sensorEvents?.userAccount?.last_name
                    ? "-"
                    : <Link className="device-link" to={`/customer/view/${event?.sensorEvents?.userAccount?.id}`}>{(event?.sensorEvents?.userAccount?.first_name ? event?.sensorEvents?.userAccount?.first_name : "") +
                        " " +
                        (event?.sensorEvents?.userAccount?.last_name ? event?.sensorEvents?.userAccount?.last_name : "")}</Link>,

            id: "first_name",
            className: "long-text-devices"
        },
    ]

    const fetchAllEvents = async () => {
        try {
            const params = {
                page: 1,
                search: debounceText,
                limit: 100000,
                sort: sort,
                sortCol: sortCol,
            };
            const response = await request<SensorSOSEventsResponse>({
                url: "/adminCommon/getSensorEvents",
                method: "POST",
                data: params,
            });
            if (response.success) {
                return response.data;
            } else {
                toast.error("Failed to fetch all Events: " + response.message);
                return [];
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`);
            return [];
        }
    };

    const exportCSV = async () => {
        const allData = await fetchAllEvents();
        if (!allData.length) {
            toast.error("No data available to export.");
            return;
        }

        // Match your columns array order
        const headers = [
            "Sr No.",
            "Event Type",
            "Execute Time",
            "Sensor Name",
            "Sensor or SOS",
            "Location Name",
            "Address",
            "User Name"
        ];

        const rows = allData.map((event: SensorSOSEventsItem, index: number) => {
            const executeTime = event?.executeTime
                ? (() => {
                    const date = new Date(event.executeTime);
                    const options: Intl.DateTimeFormatOptions = {
                        timeZone: "America/New_York",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                        month: "short",
                        day: "numeric",
                    };
                    return `${date.toLocaleString("en-US", options)} ET`;
                })()
                : "-";

            const userName =
                !event?.sensorEvents?.userAccount?.first_name &&
                    !event?.sensorEvents?.userAccount?.last_name
                    ? "-"
                    : `${event?.sensorEvents?.userAccount?.first_name || ""} ${event?.sensorEvents?.userAccount?.last_name || ""
                        }`.trim();

            return [
                index + 1,
                event?.eventName || "-",
                executeTime,
                event?.sensorEvents?.name || "-",
                event?.sensorEvents?.is_sos === "1" ? "SOS" : "Sensor",
                event?.sensorEvents?.locationsSensor?.name || "-",
                event?.sensorEvents?.locationsSensor?.address || "-",
                userName,
            ];
        });

        donwloadCSV(headers, rows, "SensorSOSEvents.csv");
    };


    const exportPDF = async () => {
        const allData = await fetchAllEvents();
        if (!allData.length) {
            toast.error("No data available to export.");
            return;
        }

        const headers = [
            "Sr No.",
            "Event Type",
            "Execute Time",
            "Sensor Name",
            "Sensor or SOS",
            "Location Name",
            "Address",
            "User Name",
        ];

        const rows = allData.map((event: SensorSOSEventsItem, index: number) => {
            // Format Execute Time like your UI
            const executeTime = event?.executeTime
                ? (() => {
                    const date = new Date(event.executeTime);
                    const options: Intl.DateTimeFormatOptions = {
                        timeZone: "America/New_York",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                        month: "short",
                        day: "numeric",
                    };
                    return `${date.toLocaleString("en-US", options)} ET`;
                })()
                : "-";

            // Combine user name
            const userName =
                !event?.sensorEvents?.userAccount?.first_name &&
                    !event?.sensorEvents?.userAccount?.last_name
                    ? "-"
                    : `${event?.sensorEvents?.userAccount?.first_name || ""} ${event?.sensorEvents?.userAccount?.last_name || ""
                        }`.trim();

            return [
                index + 1,
                event?.eventName || "-",
                executeTime,
                event?.sensorEvents?.name || "-",
                event?.sensorEvents?.is_sos === "1" ? "SOS" : "Sensor",
                event?.sensorEvents?.locationsSensor?.name || "-",
                event?.sensorEvents?.locationsSensor?.address || "-",
                userName,
            ];
        });

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "A4",
        });

        // Title
        doc.setFontSize(16);
        doc.text("Sensor SOS Events Report", 40, 40);

        // Table
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 60,
            styles: {
                fontSize: 9,
                cellPadding: 5,
                lineColor: [230, 230, 230],
                lineWidth: 0.5,
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245],
            },
            columnStyles: {
                0: { cellWidth: 40 },
                1: { cellWidth: 90 },
                2: { cellWidth: 90 },
                3: { cellWidth: 90 },
                4: { cellWidth: 70 },
                5: { cellWidth: 100 },
                6: { cellWidth: 150 },
                7: { cellWidth: 90 },
            },
            didDrawPage: (data) => {
                doc.setFontSize(10);
                const pageCount = doc.getNumberOfPages();
                doc.text(
                    `Page ${data.pageNumber} of ${pageCount}`,
                    doc.internal.pageSize.getWidth() - 80,
                    doc.internal.pageSize.getHeight() - 20
                );
            },
        });

        doc.save("SensorSOSEvents.pdf");
    };


    return (
        <section className=' dashboard-section dashboard-item'>
            <ToastContainer position='top-right' autoClose={2000} theme='light' />

            <div className='container'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='dashboard-table-title-section'>
                            <h2>Sensor and SOS Events</h2>
                            <div className='dashboard-table-title-right-side d-flex gap-2'>
                                <PrimaryButton
                                    label='Export CSV'
                                    className='max-w-max-content'
                                    onClick={exportCSV}
                                />
                                <PrimaryButton
                                    label='Export PDF'
                                    className='max-w-max-content'
                                    onClick={exportPDF}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='col-12 mt-4'>
                        <div className='table-container-main'>
                            <div className='table-details-section'></div>
                            <Table
                                rowData={sensorEvents}
                                columns={columns}
                                page={page}
                                setPage={setPage}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                totalItems={totalItems}
                                setSortColumn={setSortCol}
                                setSortOrder={setSort}
                                sortColumn={sortCol}
                                sortOrder={sort}
                                isSearch
                                search={search}
                                setSearch={setSearch}
                                multipleFilters={multipleFilters}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SensorSOSEvents
